import React, { useContext, useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { customSizes, palette } from '../../assets/styles/themes/tokens';
import ReaderDetailsWrapper from './ReaderDetails/ReaderDetailsWrapper';
import { ReaderContext } from '../Reader/ReaderContext';
import { selectMindItem } from '../../reducers/mindItems.selectors';
import { useSelector } from 'react-redux';
import Toolbar from './Toolbar/Toolbar';
import { useTranslation } from 'react-i18next';
import services from '../../services';
import { DownloadInformationData } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { noop } from 'lodash';
import initFileDownload from '../utils/downloadIFrame';
import useAction from '../../utils/useAction';
import { incrementDownloadsCounter as incrementDownloadsCounterAction } from '../../actions/user.actions';
import { storeDownloadedItem as storeDownloadedItemAction } from '../../actions/download.actions';
import { PinAction } from '../cards/MuiCard/MuiCardBase/MuiCardBase';
import {
  selectOwnCollectionStatus as selectOwnCollectionStatusAction,
  selectIsItemInWhichOwnCollections as selectIsItemInWhichOwnCollectionsAction,
} from '../../reducers/collections.selectors';
import { AsyncActionStatus } from '../../actions/common.actionTypes';
import _ from 'lodash';
import ReaderContent from './ReaderContent/ReaderContent';
import useScreenSize from '../../utils/useScreenSize';
import PagePreviewList from './PagePreviewList';
import { getAmpliDeviceId, getAmpliSessionId, mapDesignPreference } from '../../utils/ampli';
import NotificationAlert from '../NotificationAlert';
import { useNewDesign } from '../../state/DesignPreferenceContextProvider';
import { trackContentViewed } from '../../utils/tracking';
import { selectUser } from '../../reducers/user.selectors';
import FloatingToolbar from './FloatingToolbar/FloatingToolbarBase';
import ReturnTopButton from './ReturnTopButton/ReturnTopButton';
import NewReaderFeedbackDialog from '../modals/FeedbackDialogs/NewReaderFeedbackDialog';
import { onboardingHints } from '../../constants';
import { Alert } from '../NotificationAlert/NotificationAlert';
import { TFunction } from 'i18next';
import AIAssistantDialog from './AIAssistant/AIAssistantDialog';
import { aiAssistantContext } from '../../state/AIAssistantContextProvider';

const getDownloadAlerts = (t: TFunction, leftDownloads: number, downloadLimit: number): { [key: string]: Alert } => ({
  downloadSuccessAlert: {
    message: t('You still have <number> of <limit> downloads left this month',
      { number: leftDownloads, limit: downloadLimit }),
    severity: 'success',
  },
  downloadLimitReached: {
    message: t('Your download limit for this month has been reached. '
      + 'Starting next month you will be able to download new materials again.'),
    severity: 'warning',
  },
});

export const getPinnedAlerts = (t: TFunction): { [key: string]: Alert } => ({
  addedToFavAlert: {
    message: t('The material has been successfully added to the favorites.'),
    severity: 'success',
  },
  removedFromFavAlert: {
    message: t('The material has been successfully removed from the favorites.'),
    severity: 'success',
  },
});

const getCollectionAlerts = (t: TFunction): { [key: string]: Alert } => ({
  updatedCollections: {
    message: t('Your changes have been successfully saved.'),
    severity: 'success',
  },
});

const NewReader = () => {
  const [downloadInfo, setDownloadInfo] = useState({} as DownloadInformationData);
  const [downloadTriggered, setDownloadTriggered] = useState(false);  // prevent multiple downloads from being counted
  const { t } = useTranslation();
  const { isLargeScreen: isDesktop } = useScreenSize();
  const {
    currentPage,
    mindItemId,
    pspdfkitInstance,
    fullScreenMode,
    alert,
    setAlert,
  } = useContext(ReaderContext);
  const mindItem = useSelector(state => selectMindItem(state, mindItemId));
  const incrementDownloadsCounter = useAction(incrementDownloadsCounterAction);
  const storeDownloadedItem = useAction(storeDownloadedItemAction);
  const selectOwnCollectionStatus = useSelector(state => selectOwnCollectionStatusAction(state));
  const selectIsItemInWhichOwnCollections: string[] =
    useSelector(state => selectIsItemInWhichOwnCollectionsAction(state, mindItemId));
  const [prevCollections, setPrevCollections] = 
    useState<{mindItemId?: string, collections?: string[]}>({});
  const { prefersNewDesign, shouldShowDesignPreferenceSwitch } = useNewDesign();
  const user = useSelector(selectUser);
  const [isFirstSession, setIsFirstSession] = useState(true);  // Default value should hide the feedback dialog
  const { isAssistantAvailable } = useContext(aiAssistantContext);

  const onPinActionCallback = (action: PinAction) => {
    setAlert?.(action === 'pin' ? alerts.addedToFavAlert : alerts.removedFromFavAlert);
  };

  const trackingInfo = {
    sessionId: getAmpliSessionId(),
    deviceId: getAmpliDeviceId(),
    ui: mapDesignPreference(prefersNewDesign),
  };

  useEffect(() => {
    const isFirstVisit = !localStorage.getItem(onboardingHints.newReaderFeedback.isFirstVisit);
    const isFirstSession = sessionStorage.getItem(onboardingHints.newReaderFeedback.isFirstSession)  === 'true';
  
    if (isFirstVisit) {
      localStorage.setItem(onboardingHints.newReaderFeedback.isFirstVisit, 'true');
      sessionStorage.setItem(onboardingHints.newReaderFeedback.isFirstSession, 'true');
    } else {
      localStorage.setItem(onboardingHints.newReaderFeedback.isFirstVisit, 'false');
      if (isFirstSession) {
        setIsFirstSession(true);
      } else {
        setIsFirstSession(false);
        sessionStorage.setItem(onboardingHints.newReaderFeedback.isFirstSession, 'false');
      }
    }
  }, []);

  const onCloseFeedbackModal = () => {
    localStorage.setItem(onboardingHints.newReaderFeedback.isClosed, 'true');
  };

  const onRemindLaterFeedbackModal = () => {
    sessionStorage.setItem(onboardingHints.newReaderFeedback.isFirstSession, 'true');
  };

  const isFeedbackModalClosed = localStorage.getItem(onboardingHints.newReaderFeedback.isClosed) === 'true';

  useEffect(() => {
    if (selectOwnCollectionStatus === AsyncActionStatus.READY) {
      const isFirstRender = prevCollections.collections === undefined;
      const hasMindItemChanged = prevCollections.mindItemId !== mindItemId;
      const hasCollectionsChanged = !_.isEqual(prevCollections.collections, selectIsItemInWhichOwnCollections);

      if (hasCollectionsChanged) {
        setPrevCollections({ mindItemId, collections: selectIsItemInWhichOwnCollections });
        if (!isFirstRender && !hasMindItemChanged) {
          setAlert?.(alerts.updatedCollections);
        }
      }
    }
  }, [selectIsItemInWhichOwnCollections]);

  const leftDownloads = downloadInfo.downloadLimit - downloadInfo.counter -
    (downloadInfo?.mainDocument?.countsTowardsLimit ? 1 : 0);  // it does not count if doc is free or already downloaded

  const alerts: { [key: string]: Alert } = {
    ...getDownloadAlerts(t, leftDownloads, downloadInfo.downloadLimit),
    ...getPinnedAlerts(t),
    ...getCollectionAlerts(t),
  };

  useEffect(() => {
    services.mindItem
      .getDownloadInformation(mindItemId, trackingInfo)
      .then((data: DownloadInformationData) => {
        setDownloadInfo(data);
      })
      .catch(noop);

    setDownloadTriggered(false);
    if (mindItem && user) {  // user is needed to track server side
      trackContentViewed(mindItem, prefersNewDesign, shouldShowDesignPreferenceSwitch);
    }
  }, [mindItemId]);

  const downloadMindItem = () => {
    if (leftDownloads < 0) {
      setAlert?.(alerts.downloadLimitReached);
    } else if (downloadInfo.canDownload && downloadInfo.mainDocument?.canDownload && downloadInfo.mainDocument?.link) {
      initFileDownload(downloadInfo.mainDocument.link);
      storeDownloadedItem(downloadInfo.mainDocument.id);

      if (!downloadTriggered && downloadInfo.mainDocument.countsTowardsLimit) {
        incrementDownloadsCounter();
      }

      setDownloadTriggered(true);
      setAlert?.(alerts.downloadSuccessAlert);
    }
  };

  const renderAlertComponent = (alert: Alert) => {
    return (
      <NotificationAlert
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert?.(undefined)}
        alertSx={{ width: '100%' }}
      />
    );
  };

  const returnTop = () => {
    pspdfkitInstance?.setViewState(viewState => viewState.set('currentPageIndex', 0));
  };

  if (!mindItem) return null;

  return <Box
    height='100%'
    width='100%'
    sx={{
      backgroundColor: palette.background.default,
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Toolbar mindItem={mindItem} downloadMindItem={downloadMindItem} pinActionCallback={onPinActionCallback} />
    <Box
      sx={{
        position: 'relative',
        paddingX: customSizes.readerPage.paddingX,
        display: 'flex',
        flexGrow: 1,
        overflow: 'hidden',
      }}>
      {
        <>
          {isDesktop && <ReaderDetailsWrapper />}
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              height: '100%',
              position: 'relative',
              marginX: (isDesktop && !fullScreenMode) ? customSizes.pdfArea.paddingX : 0,
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            {alert && renderAlertComponent(alert)}
            <ReaderContent />
            <FloatingToolbar mindItem={mindItem} />
            {currentPage > 1 && <ReturnTopButton
              onClick={returnTop} 
              isFullScreenMode={!!fullScreenMode}
            />}
          </Box>
          {isDesktop && <PagePreviewList />}
        </>
      }
    </Box>
    <NewReaderFeedbackDialog 
      isDocument={mindItem.fileType === 'document'} 
      isFirstSession={isFirstSession} 
      isClosed={isFeedbackModalClosed} 
      onClose={onCloseFeedbackModal}
      onRemindLater={onRemindLaterFeedbackModal}
    />
    {isAssistantAvailable && <AIAssistantDialog />}
  </Box>;
};

export default NewReader;
